import { useMemo } from 'react';

/** Mark: an envelope riding a wave. */
export function Logo({ size = 34 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <defs>
        <linearGradient id="mo-g1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8be3ec" />
          <stop offset="0.5" stopColor="#3aa0ff" />
          <stop offset="1" stopColor="#0b5cad" />
        </linearGradient>
        <linearGradient id="mo-g2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#17c3cf" />
          <stop offset="1" stopColor="#0b5cad" />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="13" fill="url(#mo-g1)" />
      <rect x="11" y="12" width="26" height="18" rx="4" fill="#fff" fillOpacity="0.95" />
      <path d="m12.5 14 11.5 8.5L35.5 14" fill="none" stroke="#0b5cad" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M0 34c4 0 4-3 8-3s4 3 8 3 4-3 8-3 4 3 8 3 4-3 8-3 4 3 8 3v14H0Z" fill="url(#mo-g2)" />
      <path d="M0 39c4 0 4-2.5 8-2.5s4 2.5 8 2.5 4-2.5 8-2.5 4 2.5 8 2.5 4-2.5 8-2.5 4 2.5 8 2.5" fill="none" stroke="#fff" strokeOpacity="0.55" strokeWidth="1.6" />
    </svg>
  );
}

function wavePath(amp: number, base: number, len: number) {
  // Two identical periods so translating by -50% loops seamlessly.
  let d = `M0 ${base}`;
  for (let x = 0; x < 2880; x += len) {
    d += ` Q ${x + len / 4} ${base - amp} ${x + len / 2} ${base} T ${x + len} ${base}`;
  }
  return `${d} V 400 H 0 Z`;
}

/** Animated open-water backdrop: three wave layers and envelopes rising like bubbles. */
export function OceanBackdrop({ letters = 14 }: { letters?: number }) {
  const bubbles = useMemo(
    () =>
      Array.from({ length: letters }, (_, i) => ({
        left: `${(i * 37 + 7) % 100}%`,
        size: 12 + ((i * 7) % 16),
        duration: 14 + ((i * 5) % 16),
        delay: -((i * 3.7) % 20),
      })),
    [letters],
  );
  return (
    <>
      <div className="bubbles" aria-hidden="true">
        {bubbles.map((b, i) => (
          <svg key={i} className="bubble" style={{ left: b.left, width: b.size, height: b.size, animationDuration: `${b.duration}s`, animationDelay: `${b.delay}s` }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <rect x="3" y="5" width="18" height="14" rx="3" />
            <path d="m4 7 8 6 8-6" />
          </svg>
        ))}
      </div>
      <div className="ocean-waves" aria-hidden="true">
        <svg className="w3" viewBox="0 0 2880 400" preserveAspectRatio="none"><path d={wavePath(26, 140, 480)} fill="#17c3cf" /></svg>
        <svg className="w2" viewBox="0 0 2880 400" preserveAspectRatio="none"><path d={wavePath(34, 200, 720)} fill="#1a7fe0" /></svg>
        <svg className="w1" viewBox="0 0 2880 400" preserveAspectRatio="none"><path d={wavePath(22, 270, 360)} fill="#0b5cad" /></svg>
      </div>
    </>
  );
}

/** The envelope that "launches" into the ocean in the wizard. */
export function LetterLaunch({ flying }: { flying: boolean }) {
  return (
    <div className={`letter-launch${flying ? ' flying' : ''}`}>
      <span className="ring" />
      <span className="ring" />
      <span className="ring" />
      <svg className="env" width="86" height="64" viewBox="0 0 86 64" aria-hidden="true">
        <defs>
          <linearGradient id="env-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="1" stopColor="#d6ebff" />
          </linearGradient>
        </defs>
        <rect x="2" y="2" width="82" height="60" rx="10" fill="url(#env-g)" />
        <path d="m6 8 37 27L80 8" fill="none" stroke="#0b5cad" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="70" cy="48" r="7" fill="#17c3cf" />
      </svg>
    </div>
  );
}
